import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  Language,
  useUserSignUpMutation,
} from "../../graphql/generated/graphql";
import {
  convertPersianNumbers,
  convertToPersianDigits,
} from "../../utils/convertPersianNumber";
import Verify from "../../components/Verify/Verify";
import CardModal from "../../components/Modal/CardModal";
import LoadingButton from "../../components/Button/LoadingButton";
import { Link } from "react-router-dom";
import campaignImage from "../../image/campaign.webp";

const signUpMessages = [
  "phone_exists_code_sent",
  "phone_exists_code_already_sent",
];

export default function SignIn() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userSignUpMutation, { loading: codeLoading, error: codeError }] =
    useUserSignUpMutation();

  const [isAlreadySignedUp, setIsAlreadySignedUp] = useState(false);

  const formik = useFormik({
    initialValues: { phoneNumber: "" },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .required("شماره موبایل الزامی است")
        .test("is-phone", "فرمت شماره موبایل معتبر نیست", (value) => {
          if (!value) return false;
          const enValue = convertPersianNumbers(value);
          return /^09\d{9}$/.test(enValue);
        }),
    }),
    onSubmit: async (values) => {
      const enPhoneNumber = convertPersianNumbers(values.phoneNumber);
      const firstName = enPhoneNumber.slice(0, 4);
      const lastName = enPhoneNumber.slice(4);

      try {
        const { data } = await userSignUpMutation({
          variables: {
            input: {
              phoneNumber: enPhoneNumber,
              translations: [
                {
                  firstname: firstName,
                  lastname: lastName,
                  lang: Language.Fa,
                },
              ],
            },
          },
        });

        if (!data?.userSignUp?.success) {
          if (signUpMessages.includes(data?.userSignUp?.message!)) {
            setIsAlreadySignedUp(true);
          }
        }

        setIsModalOpen(true);
        setPhoneNumber(enPhoneNumber);
        toast.success("کد اعتبار سنجی ارسال شد");
      } catch (err) {
        toast.error("خطا در ارسال کد");
        formik.setFieldError("phoneNumber", "خطا در ارسال کد");
      }
    },
  });

  const handleEditPhoneNumber = () => {
    setIsModalOpen(false);
    formik.setFieldValue("phoneNumber", convertToPersianDigits(phoneNumber));
  };

  return (
    <main className="min-h-screen bg-slate-50 flex flex-col justify-center items-center py-6 px-4 sm:py-12 sm:px-6">
      <form
        onSubmit={formik.handleSubmit}
        className="w-full max-w-md bg-white rounded-2xl border border-gray-200/90 shadow-md shadow-gray-200/50 overflow-hidden"
      >
        {/* Campaign Banner Header */}
        <div className="w-full overflow-hidden bg-emerald-50/50 border-b border-gray-100">
          <img
            src={campaignImage}
            alt="رویداد کار و اشتغال پذیری دانشگاهی"
            className="w-full h-auto object-cover max-h-56 sm:max-h-64"
            loading="eager"
          />
        </div>

        <div className="p-4 sm:p-6 flex flex-col gap-4">
          {/* Header & Subtitle */}
          <div className="space-y-1.5 text-center">
            <h1 className="text-base sm:text-lg font-bold text-primary leading-snug">
              ثبت نام در نمایشگاه کار و اشتغال پذیری دانشگاهی
            </h1>
            <p className="text-xs sm:text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/70 rounded-lg px-3 py-1.5 inline-block">
              استخدام و شبکه سازی دانشجویان و شرکت های دانش بنیان
            </p>
          </div>

          {/* Campaign Description Paragraph 1 */}
          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed text-justify bg-gray-50/90 rounded-xl p-3 border border-gray-200/70">
            نمایشگاه کار ۲ روزه در دانشگاه آزاد اسلامی واحد البرز در تاریخ ۲ و ۳
            آذر ماه و دانشگاه علوم پزشکی ایران در تاریخ ۱۵ و ۱۶ آذر ماه امسال با
            حمایت ویژه صندوق نوآوری و شکوفایی از شرکت های دانش بنیان برای حضور
            رایگان در مجموعه رویداد های اشتغال پذیری
          </p>

          {/* Call to action Paragraph 2 */}
          <p className="text-xs sm:text-sm text-gray-800 leading-relaxed font-medium">
            برای ثبت نام و رزرو قطعی غرفه شماره همراه خود را وارد کنید و بعد از
            ورود با تکمیل فرم مشخصات شرکت و افزودن فرصت های شغلی شرکت از
            امتیازات ویژه این طرح برای جذب نیروی کار متخصص استفاده کنید:
          </p>

          {/* Phone Number Input */}
          <div className="pt-1">
            <label
              htmlFor="phoneNumber"
              className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5"
            >
              شماره موبایل خود را وارد کنید
            </label>
            <input
              type="text"
              id="phoneNumber"
              name="phoneNumber"
              value={formik.values.phoneNumber}
              onChange={(e) => {
                const convertedValue = convertToPersianDigits(e.target.value);
                formik.setFieldValue("phoneNumber", convertedValue);
              }}
              onBlur={formik.handleBlur}
              placeholder="۰۹۱۲XXXXXXX"
              dir="ltr"
              className="text-sm text-left block w-full rounded-lg border border-gray-300 py-2.5 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            />

            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <span className="text-red-600 text-xs mt-1 block">
                {formik.errors.phoneNumber}
              </span>
            )}

            <p className="text-slate-500 text-xs mt-2.5">
              شرایط استفاده از خدمات و حریم خصوصی این{" "}
              <span className="text-blue-700 font-medium">
                <Link to={"/privacy"} target="_blank">
                  سرویس
                </Link>
              </span>{" "}
              را می‌پذیرم.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full flex justify-center items-center rounded-xl bg-primary py-2.5 sm:py-3 px-4 text-sm font-bold text-white shadow-sm hover:bg-opacity-90 active:scale-[0.99] transition-all disabled:opacity-60"
            disabled={codeLoading}
          >
            {codeLoading ? <LoadingButton /> : "تایید"}
          </button>

          {codeError && (
            <p className="text-red-600 text-xs text-center">
              خطا در ارسال کد: {codeError.message}
            </p>
          )}
        </div>
      </form>

      <CardModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        request={undefined}
      >
        <Verify
          isAlreadySignedUp={isAlreadySignedUp}
          phoneNumber={phoneNumber}
          onEditPhoneNumber={handleEditPhoneNumber}
        />
      </CardModal>
    </main>
  );
}
